// Atomic: Data loading utilities

// Cache keys for localStorage
const CACHE_KEYS = {
  ENERGY_DATA: 'energy_data_cache',
  GEMEINDEN: 'gemeinden_cache',
  PLZ_MAPPING: 'plz_mapping_cache',
  TIMESTAMP: 'energy_data_timestamp'
} as const;

// Cache TTL in milliseconds (1 hour)
const CACHE_TTL = 60 * 60 * 1000;

/**
 * Load JSON data from a URL
 */
// Only override fetch in browser environment
if (typeof window !== 'undefined') {
  // Store the original fetch
  const originalFetch = window.fetch;

  // Override the global fetch to suppress logs
  window.fetch = async function(...args) {
    // Call the original fetch
    const response = await originalFetch.apply(this, args);
    
    // Clone the response so we can read it without consuming it
    const clonedResponse = response.clone();
    
    // Log only errors
    if (!clonedResponse.ok) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.toString() || 'unknown URL';
      console.error(`Error fetching ${url}:`, clonedResponse.statusText);
    }
    
    return response;
  };
}

export async function loadJsonData<T>(path: string): Promise<T> {
  try {
    // Add cache buster in development to force fresh data
    const url = process.env.NODE_ENV === 'development' 
      ? `${path}?t=${Date.now()}`
      : path;
      
    const response = await fetch(url);
    
    if (!response.ok) {
      const error = new Error(`Failed to load ${path}: ${response.statusText}`);
      console.error(error);
      throw error;
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error in loadJsonData for ${path}:`, error);
    throw error;
  }
}

/**
 * Save data to localStorage with a timestamp
 */
export function saveToLocalStorage<T>(key: string, data: T): void {
  if (typeof window === 'undefined') return;
  
  try {
    const item = {
      data,
      timestamp: new Date().getTime()
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

/**
 * Load data from localStorage
 */
export function loadFromLocalStorage<T>(key: string): { data: T | null; isExpired: boolean } {
  if (typeof window === 'undefined') return { data: null, isExpired: true };
  
  try {
    const item = localStorage.getItem(key);
    if (!item) return { data: null, isExpired: true };
    
    const parsed = JSON.parse(item);
    const isExpired = new Date().getTime() - parsed.timestamp > CACHE_TTL;
    
    return { data: parsed.data, isExpired };
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return { data: null, isExpired: true };
  }
}

// Re-export CACHE_KEYS and CACHE_TTL for use in other modules
export { CACHE_KEYS, CACHE_TTL };
